import { useAtom } from 'jotai';
import {
  cx,
  DropdownMenu,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownSeparator,
  Icon,
} from '../../ui';
import {
  DEFAULT_SIDEBAR_ICONS,
  normaliseSidebarIcons,
  normaliseSidebarSize,
  normaliseSymbolStyle,
  normaliseSymbolStyleMode,
  normaliseSymbolTint,
  normaliseThemePreference,
  type SidebarIcons,
  type SidebarSize,
  SYMBOL_STYLE_MODES,
  SYMBOL_TINTS,
  type SymbolStyle,
  type SymbolStyleMode,
  type SymbolTint,
  sidebarIconsAtom,
  sidebarSizeAtom,
  symbolStyleAtom,
  symbolStyleModeAtom,
  symbolTintAtom,
  type ThemePreference,
  themeAtom,
  useResolvedTheme,
  useSetAppearance,
  useSetSidebarIcons,
  useSetSidebarSize,
  useSetSymbolStyle,
  useSetSymbolStyleMode,
  useSetSymbolTint,
} from '../session';
import {
  ICON_OPTIONS,
  MODE_LABELS,
  SIZE_OPTIONS,
  STYLE_OPTIONS,
  styleModeHeading,
  THEME_OPTIONS,
  TINT_FILLS,
  TINT_LABELS,
} from './appearanceOptions';
import { navItem } from './navItem';

/**
 * THE APPEARANCE MENU: the four settings macOS keeps on one pane, in one menu.
 *
 * It began as the appearance alone - Light, Dark, Automatic, per PERSON since
 * 2026-09-07 - and grew on 2026-09-09 into the pane Apple actually ships:
 * Appearance, then "Icon style" with its Always/Auto, then the "Theme colour" the
 * tinted style is tinted with, then "Sidebar icon size". Every label is Apple's
 * own word for the setting, because a person who has met the setting on their Mac
 * should not have to work out that we renamed it.
 *
 * IT WAS A BLIND THREE-STATE CYCLE and it had to stop being one. One button
 * advanced automatic -> light -> dark, which asks the user to press a control to
 * find out what it does and then press it twice more to undo that. Its third
 * face was the settings-sliders glyph, so the control also announced the wrong
 * thing: Arif hit it, saw sliders where a sun had been, and read it as a bug.
 * That reading was correct - an icon that means "settings" everywhere else in
 * this product cannot mean "follow the OS" here.
 *
 * A MENU IS THE HONEST SHAPE for a set of states: all of them are visible at once,
 * each is named in words, and the current one carries a tick. Nothing has to be
 * discovered by pressing it.
 *
 * THE TRIGGER SHOWS THE EFFECTIVE THEME, never the preference - a sun in light,
 * a moon in dark, and under "Automatic" whichever one the OS is actually giving.
 * There is deliberately no third glyph: the trigger's job is to say what you are
 * looking at, and the menu's job is to say why.
 *
 * AND IT IS A SIDEBAR ROW WITH A WORD ON IT (SH-A6, owner-60: "Home Button der Mond und
 * alles ist komisch positioniert"). It was a glass capsule carrying a lone moon, sitting
 * in the sidebar's footer beside an avatar: a control in a column of rows, and a symbol
 * with nothing to say what it does. No Apple app puts an appearance switch in its own
 * sidebar at all - macOS puts it in System Settings > Appearance
 * (https://support.apple.com/guide/mac-help/use-a-light-or-dark-appearance-mchl52e1c2d2/mac),
 * searched against the Notes, Books, Reminders, Mail and Photos handbooks - and this
 * product has no Appearance pane yet, so the parent's decision for this round is that the
 * control stays in the shell and is DRAWN as what it stands in: an ordinary labelled row,
 * the same construction as the Sign out row under it, opening the same menu. The move into
 * a settings pane is a roadmap line, not a value invented here.
 *
 * THE THEME COLOUR IS ONLY THERE WHEN IT DOES SOMETHING. It tints the Tinted style
 * and nothing else, so under the other three it is not a disabled row - it is not a
 * row. Apple's own pane does the same: the swatches belong to Tinted.
 *
 * Keyboard: the trigger is a real button and every row is a Radix menu item, so
 * Enter/Space opens it, arrows move, Enter selects and Escape closes - the whole
 * model comes from the primitive rather than from anything written here.
 *
 * SINCE 2026-10-04 IT IS THE PHONE'S MENU ONLY (redesign register L15, A8-7). On the desktop
 * every appearance setting lives in the account menu (`AccountMenu.tsx`): Light, Dark and
 * Automatic, and under them one submenu per sidebar setting; the phone's More sheet keeps this
 * one menu with all of them until the phone round (P6). The words are shared
 * (`appearanceOptions.ts`), and "Small" left the sizes everywhere (R-S4, L7).
 */
export function ThemeToggle() {
  const [storedTheme] = useAtom(themeAtom);
  /** A value written before the rename reads as Automatic rather than as nothing. */
  const preference = normaliseThemePreference(storedTheme) ?? 'automatic';
  const [storedStyle] = useAtom(symbolStyleAtom);
  const style = normaliseSymbolStyle(storedStyle) ?? 'default';
  const [storedMode] = useAtom(symbolStyleModeAtom);
  const mode = normaliseSymbolStyleMode(storedMode) ?? 'auto';
  const modes = SYMBOL_STYLE_MODES[style];
  /**
   * A mode the CURRENT style does not offer reads as Auto, which is what the resolver does
   * with it too: someone who had Dark on Always and switches to Clear has chosen nothing
   * about Clear yet, and Auto is the entry Apple's pane starts every style on.
   */
  const activeMode = modes.includes(mode) ? mode : 'auto';
  const [storedTint] = useAtom(symbolTintAtom);
  const tint = normaliseSymbolTint(storedTint) ?? 'multicolour';
  const [storedSize] = useAtom(sidebarSizeAtom);
  const size = normaliseSidebarSize(storedSize) ?? 'medium';
  const [storedIcons] = useAtom(sidebarIconsAtom);
  const icons = normaliseSidebarIcons(storedIcons) ?? DEFAULT_SIDEBAR_ICONS;

  /** Each writes the atom AND the per-person preference on the server, in that order. */
  const setPreference = useSetAppearance();
  const setStyle = useSetSymbolStyle();
  const setMode = useSetSymbolStyleMode();
  const setTint = useSetSymbolTint();
  const setSize = useSetSidebarSize();
  const setIcons = useSetSidebarIcons();
  const resolved = useResolvedTheme();

  return (
    <DropdownMenu
      trigger={
        <button
          type="button"
          // The name says what the control OPENS, not which state it is in: the
          // state is the tick inside, where a screen reader can read it as one.
          aria-label="Appearance"
          /* NO `w-full` (wave 5 round 3, package A4). `.shell-nav-row` is a flex box in a
             block column, so it already fills its parent; `width: 100%` pinned it to the
             parent's CONTENT box and cancelled the -4px margin that makes the kit's pill,
             so this row and Sign out drew a 228px pill in a column of 236px ones.
             kit: Apple macOS 27 UI Kit.sketch / Sidebars/Light/Large/Items/Level 0 -
             Selected, Background @(-4,0) 248x40 on a 240x40 row. */
          className={navItem({ isActive: false })}
        >
          <Icon name={resolved === 'dark' ? 'moon' : 'sun'} />
          <span className="flex-1 text-left">Appearance</span>
        </button>
      }
    >
      <DropdownRadioGroup
        label="Appearance"
        value={preference}
        onValueChange={(value) => setPreference(value as ThemePreference)}
      >
        {THEME_OPTIONS.map((option) => (
          <DropdownRadioItem key={option.value} value={option.value}>
            {option.label}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>

      <DropdownSeparator />
      {/*
        "SIDEBAR ICONS" SITS ABOVE "ICON STYLE" (package A3, decision 3), because it decides
        WHETHER there is a tile for a style to act on: a person reads the two rows top down and
        the second one is only about the first one's Tiles. Both ways ship; Symbols is the
        default since the owner's decision of 2026-10-04 (redesign register A5-2).
      */}
      <DropdownRadioGroup
        label="Sidebar icons"
        value={icons}
        onValueChange={(value) => setIcons(value as SidebarIcons)}
      >
        {ICON_OPTIONS.map((option) => (
          <DropdownRadioItem key={option.value} value={option.value}>
            {option.label}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>
      {/*
        AND THE TILE GROUPS ARE ABSENT WHILE THERE IS NO TILE, not present and disabled - the
        same rule the mode group already follows for a style that has no modes. The stored
        style, mode and tint are untouched: hiding a group is not clearing what it holds, so a
        round trip through Symbols gives a person their own tiles back (decision 2).
      */}
      {icons === 'tiles' && (
        <DropdownRadioGroup
          label="Icon style"
          value={style}
          onValueChange={(value) => setStyle(value as SymbolStyle)}
        >
          {STYLE_OPTIONS.map((option) => (
            <DropdownRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      )}
      {/*
        EACH STYLE OFFERS THE MODES APPLE GIVES IT (SH-A5). The pane used to show one Auto
        tick for all four, on the single rule "the chosen style in the dark appearance and
        Default in the light one" - which is Dark's rule and nobody else's. Apple gives
        Dark `Always | Auto`, Clear `Light | Dark | Auto`, and Default and Tinted no mode
        at all, so the group is the style's own list and it is absent where the style has
        none rather than present and disabled.
      */}
      {icons === 'tiles' && modes.length > 0 && (
        <DropdownRadioGroup
          label={styleModeHeading(style)}
          value={activeMode}
          onValueChange={(value) => setMode(value as SymbolStyleMode)}
        >
          {modes.map((value) => (
            <DropdownRadioItem key={value} value={value}>
              {MODE_LABELS[value]}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      )}

      {icons === 'tiles' && style === 'tinted' && (
        <>
          <DropdownSeparator />
          <DropdownRadioGroup
            label="Theme colour"
            value={tint}
            onValueChange={(value) => setTint(value as SymbolTint)}
          >
            {SYMBOL_TINTS.map((value) => (
              <DropdownRadioItem key={value} value={value}>
                <span aria-hidden="true" className={cx('tint-swatch', TINT_FILLS[value])} />
                {TINT_LABELS[value]}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </>
      )}

      <DropdownSeparator />
      <DropdownRadioGroup
        label="Sidebar icon size"
        value={size}
        onValueChange={(value) => setSize(value as SidebarSize)}
      >
        {SIZE_OPTIONS.map((option) => (
          <DropdownRadioItem key={option.value} value={option.value}>
            {option.label}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>
    </DropdownMenu>
  );
}
